import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import Button from '../ui/Button';
import axios from 'axios';
import PrivacyPolicyModal from '../legal/PrivacyPolicyModal';
import TermsModal from '../legal/TermsModal';
import LegalNoticeModal from '../legal/LegalNoticeModal';

const LandingPage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showLegalNotice, setShowLegalNotice] = useState(false);

  const handleLoginClick = () => {
    setShowLogin(true);
    setShowRegister(false);
  };

  const handleRegisterClick = () => {
    setShowRegister(true);
    setShowLogin(false);
  };

  const handleCloseModals = () => {
    setShowLogin(false);
    setShowRegister(false);
  };

  const [globalStats, setGlobalStats] = useState<{
    totalUsers: number;
    totalFragrances: number;
    totalDecants: number;
    totalFavorites: number;
    totalHouses: number;
  } | null>(null);
  
  useEffect(() => {
    const fetchGlobalStats = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/global/stats`);
        setGlobalStats(data.stats);
      } catch (err) {
        console.error('Failed to fetch global stats', err);
      }
    };
  
    fetchGlobalStats();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />
      
      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <div className="text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6"
          >
            Organize Your Scents With
          </motion.h1>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            style={{ filter: 'drop-shadow(4px 4px 6px rgba(0,0,0,0.5))' }}
            className="text-5xl md:text-9xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6 inline-block pb-4"
          >
            Scentral
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            Track, rate, and manage your fragrance collection with our premium organizer. 
            Discover seasonal preferences, performance insights, and keep detailed notes on every scent.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <Button
              size="lg"
              onClick={handleRegisterClick}
              className="px-8 py-4 text-lg"
            >
              Get Started
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleLoginClick}
              className="px-8 py-4 text-lg"
            >
              Login
            </Button>
          </motion.div>
        </div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid md:grid-cols-3 gap-8 mt-24"
        >
          {/* Stats Cards Wrapper */}
          {globalStats && (
            <div className="md:col-span-3 grid grid-cols-3 gap-8">
              <StatCard label="Users" value={globalStats.totalUsers} icon="👥" className="text-center" />
              <StatCard label="Unique Houses" value={globalStats.totalHouses} icon="🧑‍🎨" className="text-center" />
              <StatCard label="Total Fragrances" value={globalStats.totalFragrances} icon="🌸" className="text-center" />
            </div>
          )}
          <FeatureCard
            icon="📊"
            title="Smart Analytics"
            description="Get insights into your collection with seasonal ratings, performance metrics, and support for decants and clones."
          />
          <FeatureCard
            icon="🎯"
            title="Advanced Filtering"
            description="Search and filter by house, notes, ratings, seasons, and more to find the perfect fragrance for any occasion."
          />
          <FeatureCard
            icon="🌐"
            title="Collection Sharing"
            description="Easily share your collection with a personal URL and full privacy control options."
          />
          <FeatureCard
            icon="💾"
            title="Import & Export"
            description="Import your existing collection from CSV or export your data for backup and sharing."
          />
          <FeatureCard
            icon="💭"
            title="Wishlist"
            description="Add fragrances to your wishlist from any retailer and move them to your collection with a single click."
          />
          <FeatureCard
            icon="🎲"
            title="Random Scent Picker"
            description="Randomly select a scent from your collection with filters and options for fun discovery."
          />
        </motion.div>
      </main>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <LoginForm onClose={handleCloseModals} onSwitchToRegister={handleRegisterClick} />
          </motion.div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <RegisterForm onClose={handleCloseModals} onSwitchToLogin={handleLoginClick} />
          </motion.div>
        </div>
      )}
      {/* Footer */}
      <footer className="mt-16 py-6 text-center space-y-2">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-gray-500 dark:text-gray-400 text-sm"
        >
          &copy; {new Date().getFullYear()}{' '}
          <span className="font-semibold text-gray-900 dark:text-white">Scentral</span>. All rights reserved.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-xs text-gray-500 dark:text-gray-400 space-x-3"
        >
          <button
            onClick={() => setShowPrivacyPolicy(true)}
            className="hover:underline hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Privacy Policy
          </button>
          <span>•</span>
          <button
            onClick={() => setShowTerms(true)}
            className="hover:underline hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Terms & Conditions
          </button>
          <span>•</span>
          <button
            onClick={() => setShowLegalNotice(true)}
            className="hover:underline hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Legal Notice
          </button>
        </motion.div>

        {/* Legal Modals */}
        <PrivacyPolicyModal
          isOpen={showPrivacyPolicy}
          onClose={() => setShowPrivacyPolicy(false)}
        />
        <TermsModal
          isOpen={showTerms}
          onClose={() => setShowTerms(false)}
        />
        <LegalNoticeModal
          isOpen={showLegalNotice}
          onClose={() => setShowLegalNotice(false)}
        />
      </footer>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  className?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, className }) => (
  <motion.div
    whileHover={{ y: -3, scale: 1.05 }}
    className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}
  >
    <div className="text-3xl mb-2">{icon}</div>
    <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
    <div className="text-gray-600 dark:text-gray-300">{label}</div>
  </motion.div>
);

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
    >
      <div className="text-4xl mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

export default LandingPage;
